/**
 * Recently-visited pages tracker — persists a capped list of distinct
 * paths the user has landed on, scoped per-organization so switching
 * org doesn't leak the prior tenant's navigation history.
 *
 * Two public surfaces:
 *
 *   useTrackRecentlyVisited(): mount once at the app layout (MainLayout)
 *   so every navigation writes to localStorage. Uses react-router's
 *   useLocation to react to path changes.
 *
 *   getRecentlyVisited(): returns the current list synchronously.
 *   Widgets call it on mount. The write side is fire-and-forget so a
 *   direct read after render doesn't need await semantics.
 *
 * Storage shape (JSON):
 *   {
 *     <orgId>: [
 *       { path: '/attendance/reports', title: '...',  visited_at: '2026-05-10T...' },
 *       ...
 *     ]
 *   }
 *
 * Cap: 12 most-recent distinct paths per org. Longer history belongs
 * on a dedicated Activity page, not a dashboard widget.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { titleFor } from './recentlyVisitedTitles';

const STORAGE_KEY = 'svdy_recently_visited_v1';
const ORG_KEY = 'current_organization_id';
const MAX_ENTRIES = 12;

export interface VisitedPage {
  path: string;
  /** The i18n key naming this page, or null when no rule matched. Entries written
   *  before 2026-08-01 may hold a humanized English string from the old fallback;
   *  the widget treats anything that does not resolve as unnamed, so those degrade
   *  to the generic label rather than showing English. */
  title: string | null;
  visited_at: string; // ISO
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

type Store = Record<string, VisitedPage[]>;

function readStore(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Store) : {};
  } catch {
    return {};
  }
}

function writeStore(store: Store): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota exhausted or similar — not worth blowing up the app.
  }
}

function currentOrgId(): string | null {
  return localStorage.getItem(ORG_KEY);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns the recent-visit list for the active organization.
 * Returns an empty array if there's no org context or storage is empty.
 */
export function getRecentlyVisited(): VisitedPage[] {
  const orgId = currentOrgId();
  if (!orgId) return [];
  return readStore()[orgId] ?? [];
}

const SKIP_PATH_PREFIXES = [
  '/auth/',
  '/dashboard',          // skip dashboard itself — noisy
  '/onboarding',
];

function shouldTrack(path: string): boolean {
  if (!path || path === '/') return false;
  return !SKIP_PATH_PREFIXES.some((p) => path.startsWith(p));
}

/**
 * Records a new visit. Exposed so unit tests can drive the store
 * without routing; normal consumers should use useTrackRecentlyVisited.
 */
export function recordVisit(path: string, title?: string): void {
  if (!shouldTrack(path)) return;
  const orgId = currentOrgId();
  if (!orgId) return;
  const store = readStore();
  const list = store[orgId] ?? [];
  const entry: VisitedPage = {
    path,
    title: title ?? titleFor(path),
    visited_at: new Date().toISOString(),
  };
  // Dedup by path — move to front if already present.
  const without = list.filter((e) => e.path !== path);
  const next = [entry, ...without].slice(0, MAX_ENTRIES);
  store[orgId] = next;
  writeStore(store);
}

/**
 * Mount-once hook to be dropped into the app's layout component.
 * Reacts to route changes and persists them to localStorage.
 */
export function useTrackRecentlyVisited(): void {
  const location = useLocation();
  useEffect(() => {
    recordVisit(location.pathname + location.search);
  }, [location.pathname, location.search]);
}
